<template>
    <v-dialog :model-value="props.modelValue" @update:model-value="changeModelState" :max-width="width"
        @after-leave="$emit('after-leave')">
        <v-card class="rounded-lg">
            <v-card-title :class="titleClass">
                <slot name="title">Confirmar ação</slot>

                <v-btn class="d-flex" icon density="compact" size="default" variant="text" @click="onExit" :disabled="disabledExit" :style="props.buttonStyles?.exit || {}">
                    <v-icon size="default" color="#004F4F">mdi-close</v-icon>
                </v-btn>

            </v-card-title>
            
            <v-card-text :class="messageClass" style="text-align: center; padding: 32px;">
                <slot name="message">Você deseja prosseguir com essa ação?</slot>
            </v-card-text>

            <div v-if="!hideButtons" class="popUpDivider"></div>
            <v-card-actions v-if="!hideButtons" class="pa-4 px-8" :class="{ 'd-flex justify-space-between auxiliarColor': auxiliarText || auxiliarColor }">
                <v-btn class="px-6 py-5 d-flex" v-if="showAuxiliar && (auxiliarColor || auxiliarText)" :color="auxiliarColor" :disabled="disabledAuxiliar" @click="onAuxiliar" :variant="variantAuxiliar" rounded="xl" :style="props.buttonStyles?.auxiliar || {}">
                    <slot name="auxiliar-icon"></slot>
                    <span :class="{ 'ml-2': $slots['auxiliar-icon'] }">{{ auxiliarText }}</span>
                </v-btn>
                <div class="d-flex ga-4 w-100 justify-end">
                    <v-btn class="px-6 py-5 d-flex" v-if="showCancel" rounded="xl" :variant="variantCancel" :color="cancelColor" :disabled="disabledCancel" @click="onCancel" :style="props.buttonStyles?.cancel || {}">
                        <slot name="cancel-icon"></slot>
                        <span :class="{ 'ml-2': $slots['cancel-icon'] }">{{ cancelText }}</span>
                    </v-btn>
                    
                    <v-tooltip v-if="showConfirm && disabledConfirm && confirmTooltipText" 
                               :text="confirmTooltipText" 
                               location="top">
                        <template v-slot:activator="{ props }">
                            <span v-bind="props">
                                <v-btn 
                                    class="px-6 py-5 d-flex" 
                                    rounded="xl" 
                                    :variant="variantConfirm" 
                                    :color="confirmColor" 
                                    :disabled="disabledConfirm" 
                                    @click="onConfirm"
                                    :style="props.buttonStyles?.confirm || {}"
                                >
                                    <slot name="confirm-icon"></slot>
                                    <span :class="{ 'ml-2': $slots['confirm-icon'] }">{{ confirmText }}</span>
                                </v-btn>
                            </span>
                        </template>
                    </v-tooltip>
                    
                    <v-btn v-else-if="showConfirm" 
                           class="px-6 py-5 d-flex" 
                           rounded="xl" 
                           elevation="0"
                           :variant="variantConfirm" 
                           :color="confirmColor" 
                           :disabled="disabledConfirm" 
                           @click="onConfirm"
                           :style="props.buttonStyles?.confirm || {}"
                    >
                        <slot name="confirm-icon"></slot>
                        <span :class="{ 'ml-2': $slots['confirm-icon'] }">{{ confirmText }}</span>
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    hideButtons: { type: Boolean, default: false },
    width: { type: Number, default: 400 },
    messageClass: { type: String, default: 'popUpCardText' },
    titleClass: { type: String, default: 'popUpCardTitle pa-8 d-flex justify-space-between' },

    confirmText: { type: String, default: 'Confirmar' },
    confirmColor: { type: String, default: 'primary-60' },
    confirmTooltipText: { type: String, default: '' },
    variantConfirm: {
        type: String, validator: (value) => ['flat', 'text', 'elevated', 'tonal', 'outlined', 'plain'].includes(value), default: 'elevated'
    },

    cancelText: { type: String, default: 'Cancelar' },
    cancelColor: { type: String, default: 'neutral-35' },
    variantCancel: {
        type: String, validator: (value) => ['flat', 'text', 'elevated', 'tonal', 'outlined', 'plain'].includes(value), default: 'outlined'
    },

    auxiliarText: {type: String, default: ''},
    auxiliarColor: {type: String, default: ''},
    variantAuxiliar: {
        type: String, validator: (value) => ['flat', 'text', 'elevated', 'tonal', 'outlined', 'plain'].includes(value), default: 'elevated'
    },
    showConfirm: { type: Boolean, default: true },
    showCancel: { type: Boolean, default: true },
    showAuxiliar: { type: Boolean, default: false },

    disabledConfirm: { type: Boolean, default: false },
    disabledCancel: { type: Boolean, default: false },
    disabledAuxiliar: { type: Boolean, default: false },
    disabledExit: { type: Boolean, default: false },
    closeOn: { type: Array, default: () => ['confirm', 'cancel', 'auxiliar', 'exit']},

    buttonStyles: {
        type: Object,
        default: () => ({
            confirm: {},
            cancel: {},
            auxiliar: {},
            exit: {}
        })
    }
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'after-leave', 'auxiliar']);

const changeModelState = (value) => {
    emit('update:modelValue', value);
};

const closePopUp = (action) => {
    if (props.closeOn.includes(action)) {
        emit('update:modelValue', false);
    }
};

const onConfirm = () => {
    emit('confirm');
    closePopUp('confirm');
};

const onCancel = () => {
    emit('cancel');
    closePopUp('cancel');
};

const onAuxiliar = () => {
    emit('auxiliar');
    closePopUp('auxiliar');
}

const onExit = () => {
    closePopUp('exit');
}
</script>


<style scoped>
.popUpDivider {
    background-color: #E1E3E2;
    width: 100%;
    height: 1px;
}

.popUpCardTitle {
    background-color: rgb(var(--v-theme-neutral-95));
    color: rgb(var(--v-theme-neutral-35));
    font-size: 16px;
    font-weight: 500;
    padding-block: 14px !important;
}

.popUpCardText {
    color: #0E1211;
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
}

.buttonBorder {
    border: 1px solid;
}
</style>
